/**
 * RadarFlowGLLayer.ts — طبقة الرادار كما يبنيها Windy: قيمٌ لا ألوان، وحركةٌ لا ذوبان.
 *
 * ── ما قِيس من Windy نفسه (2026-09-24، شبكته وشيدره الحيّان) ─────────────────
 * - **يستعمل البلاطات**: `rdr.windy.com/radar2/composite/<وقت>/<z>/<x>/<y>/reflectivity.webp`،
 *   وأقصى مستوى 7 — يعرض بلاطة z7 عند تكبير 9 ممدّدةً أربعة أضعاف. فالدقّة ليست سرّ نعومته.
 * - **البلاطة قيمٌ لا ألوان**: 1461 قيمة مختلفة فقط في بلاطة، و87% منها (0,0,0). تُلوَّن في
 *   الشيدر من جدول (`u_tex_lookup`) **بعد** استيفاء القيمة خطّياً — فالحافّة خطٌّ ناعم بألوان
 *   السلّم نفسها، لا بقعة ألوانٍ ممزوجة كما يحدث حين تُمدَّد صورةٌ ملوّنة.
 * - **بين الإطارين حقل حركة** (`opticalflow.png` عند z5): يُزاح الإطار السابق إلى الأمام
 *   والتالي إلى الخلف ثم يُمزجان (`flowOffsetPrevious/Following` في شيدره). فتنزلق العاصفة
 *   ولا تخفت هنا لتظهر هناك.
 *
 * ── ولماذا لم ينفعنا ما قبلها ────────────────────────────────────────────────
 * كانت الحلقة أربع خانات مصادر MapLibre بصورٍ ملوّنة وذوبانٍ متقاطع بالشفافية. وذلك يعطي
 * بالضرورة ما رآه العميل: في منتصف كل خطوة عاصفتان باهتتان في موضعين — تُقرأ «رجوعاً
 * للخلف» — وكل خطوةٍ تنتظر `setTiles` ليُفرغ الكاش ويعيد الجلب، فإن تأخّر انقلب العرض
 * بدل أن يمتزج (‏`nsReady: false` في كل قياس). والتكبير يمدّد ألواناً جاهزة فتتميّع الحوافّ.
 *
 * ── التصميم ──────────────────────────────────────────────────────────────────
 * 1) كل بلاطة لكل إطار نسيجٌ بقناة واحدة، يُجلب مرّةً ويبقى: الحلقة كلّها في الذاكرة، فلا
 *    خطوة تنتظر شبكة. والبلاطة الفارغة (أغلب العالم) لا نسيج لها أصلاً.
 * 2) `prerender`: يُرسم الإطاران المحيطان باللحظة، كلٌّ في نسيجٍ بحجم الشاشة (قيمة + علامة
 *    «وصلت»). وما لم يصل بعد يُستعار من سلفه الأخشن ثم من أقرب إطار — فلا فراغ يومض.
 * 3) `render`: لكل بكسل شاشة يُقرأ حقل الحركة، ويُعيَّن السابق عند p − f·d والتالي عند
 *    p + (1−f)·d، ثم تُمزج **القيمتان** وتُلوَّنان من جدول السلّم نفسه (‏radarPalette.json).
 */

import type { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { type GLContext, createProgram, getLocations, bindTexture } from './glUtils';

export interface RadarGLFrame {
    /** الوقت بالثواني. */
    t: number;
    /** مسار شجرة بلاطات القيم تحت rasters/ (مثل `radar-values/…`). */
    prefix: string;
}

export interface RadarFlowField {
    url: string;
    /** أقصى إزاحة مرمَّزة بوحدة عرض العالم (‏FLOW_SCALE في radar_nowcast.py). */
    scale: number;
    /** الإزاحة المرمَّزة لكل كم ثانية. */
    seconds: number;
}

export interface RadarEncoding { base: number; step: number }

/** يطابق MAX_Z في radar_tiles.py — ما فوقه يُمدَّد في الشيدر كما يفعل Windy. */
const MAX_Z = 7;
/*
 * سقف النُّسج الحيّة. البلاطة قناة واحدة 256² = 64 ك.ب (وقد يخزّنها المعالج بأربع قنوات:
 * 256 ك.ب). 700 × 256 ك.ب ≈ 180 م.ب في أسوأ الأحوال — وحلقةٌ كاملة (36 إطاراً) لشاشة
 * حاسوب (نحو 15 بلاطة ذات صدى) أقلّ من 600.
 */
const CACHE_MAX = 700;
const FETCH_CONCURRENCY = 8;
/** رفعٌ إلى المعالج لكل إطار رسم — كل رفع أقلّ من ملّي ثانية، والسقف يمنع نبضةً ثقيلة. */
const UPLOADS_PER_FRAME = 24;
/** أقصى عدد بلاطات للشاشة؛ فوقه يُنزل المستوى درجة (شاشة مائلة جداً). */
const MAX_VIEW_TILES = 48;

const TILE_VS = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
uniform vec3 u_tile;        // x0, y0, span — بوحدة العالم
uniform vec3 u_uv;          // إزاحة ومقياس داخل النسيج (للاستعارة من سلفٍ أخشن)
uniform float u_worldOffset;
varying vec2 v_uv;
void main() {
    v_uv = u_uv.xy + a_pos * u_uv.z;
    vec2 w = u_tile.xy + a_pos * u_tile.z;
    gl_Position = u_matrix * vec4(w.x + u_worldOffset, w.y, 0.0, 1.0);
}`;

// القيمة في الأحمر، و«وصلت» في الأخضر: ما لم يُرسم يبقى أخضره صفراً فيعرف الشيدر
// الأخير أن هذا الإطار لا يعلم شيئاً هنا (لا أنه بلا مطر).
const TILE_FS = `
precision mediump float;
uniform sampler2D u_tex;
uniform float u_empty;
varying vec2 v_uv;
void main() {
    float v = u_empty > 0.5 ? 0.0 : texture2D(u_tex, v_uv).r;
    gl_FragColor = vec4(v, 1.0, 0.0, 1.0);
}`;

const FINAL_VS = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
uniform float u_worldOffset;
varying vec2 v_world;
void main() {
    v_world = a_pos;
    gl_Position = u_matrix * vec4(a_pos.x + u_worldOffset, a_pos.y, 0.0, 1.0);
}`;

const FINAL_FS = `
precision highp float;
varying vec2 v_world;
uniform sampler2D u_a;
uniform sampler2D u_b;
uniform sampler2D u_flow;
uniform sampler2D u_lut;
uniform vec2 u_viewport;
uniform mat2 u_jac;         // إزاحة بوحدة العالم ← إزاحة في إحداثيات الشاشة (0..1)
uniform float u_f;          // الكسر بين الإطارين
uniform float u_move;       // (tB − tA) / ثواني الحقل × مقياسه
uniform float u_hasFlow;
uniform float u_alpha;
void main() {
    vec2 uv = gl_FragCoord.xy / u_viewport;
    vec2 d = vec2(0.0);
    if (u_hasFlow > 0.5) {
        vec4 fl = texture2D(u_flow, clamp(v_world, 0.0, 1.0));
        d = (fl.rg * 255.0 - 128.0) / 127.0 * u_move * step(0.5, fl.b);
    }
    vec2 duv = u_jac * d;
    // العاصفة التي عند p الآن كانت عند p − f·d في السابق، وستكون عند p + (1−f)·d في التالي.
    vec4 A = texture2D(u_a, uv - u_f * duv);
    vec4 B = texture2D(u_b, uv + (1.0 - u_f) * duv);
    float v;
    if (A.g > 0.5 && B.g > 0.5) v = mix(A.r, B.r, u_f);
    else if (A.g > 0.5) v = A.r;
    else if (B.g > 0.5) v = B.r;
    else discard;
    // الجدول 256 خانة مفهرسة بالبايت نفسه؛ قراءته خطّياً تُكمل استيفاء القيمة.
    vec4 c = texture2D(u_lut, vec2(v * (255.0 / 256.0) + 0.5 / 256.0, 0.5));
    float a = c.a * u_alpha;
    if (a < 0.003) discard;
    gl_FragColor = vec4(c.rgb * a, a);
}`;

const enum St { Loading, Ready, Empty, Failed }

interface TileTex {
    st: St;
    tex: WebGLTexture | null;
    /** بايتات القيمة المفكوكة في العامل، تنتظر الرفع. */
    px: Uint8Array | null;
    w: number;
    h: number;
    used: number;
    tries: number;
}

interface ViewTile { z: number; x: number; y: number; off: number }

interface Target { fbo: WebGLFramebuffer; tex: WebGLTexture }

function worldY(lat: number): number {
    const s = Math.sin((Math.max(-85.05112878, Math.min(85.05112878, lat)) * Math.PI) / 180);
    return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}

export class RadarFlowGLLayer implements CustomLayerInterface {
    public readonly id: string;
    public readonly type = 'custom' as const;
    public readonly renderingMode = '2d' as const;

    private map: MaplibreMap | null = null;
    private tileProg: WebGLProgram | null = null;
    private finalProg: WebGLProgram | null = null;
    private tl: ReturnType<typeof getLocations> | null = null;
    private fl: ReturnType<typeof getLocations> | null = null;
    private quad: WebGLBuffer | null = null;
    private lutTex: WebGLTexture | null = null;
    private flowTex: WebGLTexture | null = null;
    private zeroTex: WebGLTexture | null = null;
    private targets: Target[] = [];
    private targetSize: [number, number] = [0, 0];
    private destroyed = false;

    private base: string;
    private frames: RadarGLFrame[] = [];
    private lut: Uint8Array;
    private lutDirty = true;
    private flow: RadarFlowField | null = null;
    private flowBmp: ImageBitmap | null = null;
    private flowReady = false;
    private alpha: number;

    private epoch = 0;
    private tiles = new Map<string, TileTex>();
    private inflight = 0;
    private queue: string[] = [];
    private view: ViewTile[] = [];
    private viewZ = 0;
    private stamp = 0;
    private blankSize = -1;

    /** عاملا فكّ (راجع radarTileWorker.ts)، وطلباتهما المعلّقة. */
    private workers: Worker[] = [];
    private nextWorker = 0;
    private reqId = 0;
    private pending = new Map<number, TileTex>();
    /** يُعاد بناء طابور الجلب حين يتغيّر شيء، لا في كل إطار رسم. */
    private pumpKey = '';
    private pumpDirty = true;

    /** إحصاءات للقياس (‏?radarDebug=1). */
    public stats = { fetched: 0, empty: 0, failed: 0, uploads: 0, fallbacks: 0 };

    constructor(id: string, base: string, lut: Uint8Array, alpha: number) {
        this.id = id;
        this.base = base;
        this.lut = lut;
        this.alpha = alpha;
        // حجم «لا صدى» الذي يردّ به nginx على كل بلاطة غائبة: البلاطة بالحجم نفسه فارغة،
        // فلا تُفكّ ولا تُرفع. قبل وصوله تُفكّ كلّها — أبطأ قليلاً لا أخطأ.
        fetch(`${base}blank-values.png`).then((r) => (r.ok ? r.blob() : null))
            .then((b) => { if (b) this.blankSize = b.size; }).catch(() => { /* يبقى −1 */ });
    }

    // ── واجهة ────────────────────────────────────────────────────────────────
    setFrames(frames: RadarGLFrame[]) {
        const same = frames.length === this.frames.length
            && frames.every((f, i) => f.prefix === this.frames[i].prefix && f.t === this.frames[i].t);
        if (same) return;
        this.frames = frames.slice().sort((a, b) => a.t - b.t);
        this.queue = [];
        this.pumpDirty = true;
        this.map?.triggerRepaint();
    }

    setFlow(flow: RadarFlowField | null) {
        if (flow?.url === this.flow?.url) return;
        this.flow = flow;
        this.flowReady = false;
        this.flowBmp = null;
        if (!flow) return;
        const want = flow.url;
        fetch(want).then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
            .then((b) => createImageBitmap(b, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' }))
            .then((bmp) => {
                if (this.destroyed || this.flow?.url !== want) { bmp.close(); return; }
                this.flowBmp = bmp;
                this.map?.triggerRepaint();
            })
            .catch(() => { /* بلا حقل: ذوبانٌ في فضاء القيم — أقلّ من الانزلاق، ولا يكسر شيئاً */ });
    }

    setLut(lut: Uint8Array) { this.lut = lut; this.lutDirty = true; this.map?.triggerRepaint(); }

    setEpoch(epoch: number) {
        if (epoch === this.epoch) return;
        this.epoch = epoch;
        this.map?.triggerRepaint();
    }

    /** الإطاران المحيطان بلحظةٍ والكسر بينهما. */
    private pairAt(epoch: number): { i: number; j: number; f: number } {
        const F = this.frames;
        if (!F.length) return { i: -1, j: -1, f: 0 };
        let i = 0;
        for (let k = 0; k < F.length; k++) { if (F[k].t <= epoch) i = k; else break; }
        const j = Math.min(i + 1, F.length - 1);
        const span = F[j].t - F[i].t;
        const f = span > 0 ? Math.max(0, Math.min(1, (epoch - F[i].t) / span)) : 0;
        return { i, j, f };
    }

    /**
     * هل وصلت بلاطات الشاشة كلّها للإطارين المحيطين بهذه اللحظة؟ — حارس التقدّم في
     * التشغيل: الحلقة لا تعبر إلى إطارٍ لم يصل فتعرض ما استُعير له بدلاً منه.
     */
    isReadyAt(epoch: number): boolean {
        const { i, j } = this.pairAt(epoch);
        if (i < 0) return true;
        for (const fi of i === j ? [i] : [i, j]) {
            for (const v of this.view) {
                const s = this.tiles.get(this.key(fi, v.z, v.x, v.y))?.st;
                if (s !== St.Ready && s !== St.Empty && s !== St.Failed) return false;
            }
        }
        return true;
    }

    /** نسبة ما وصل من الحلقة كلّها للشاشة الحالية (للقياس). */
    loadedShare(): number {
        if (!this.view.length || !this.frames.length) return 0;
        let ok = 0;
        for (let fi = 0; fi < this.frames.length; fi++) {
            for (const v of this.view) {
                const s = this.tiles.get(this.key(fi, v.z, v.x, v.y))?.st;
                if (s === St.Ready || s === St.Empty) ok++;
            }
        }
        return ok / (this.view.length * this.frames.length);
    }

    // ── دورة الحياة ──────────────────────────────────────────────────────────
    onAdd(map: MaplibreMap, gl: GLContext) {
        // تُعاد إضافة الطبقة نفسها بعد تبديل النمط، و`onRemove` قبلها أفرغ كل شيء.
        this.destroyed = false;
        this.targets = [];
        this.flowTex = null;
        this.map = map;

        this.tileProg = createProgram(gl, TILE_VS, TILE_FS);
        this.tl = getLocations(gl, this.tileProg, ['a_pos'],
            ['u_matrix', 'u_tile', 'u_uv', 'u_worldOffset', 'u_tex', 'u_empty']);
        this.finalProg = createProgram(gl, FINAL_VS, FINAL_FS);
        this.fl = getLocations(gl, this.finalProg, ['a_pos'],
            ['u_matrix', 'u_worldOffset', 'u_a', 'u_b', 'u_flow', 'u_lut', 'u_viewport', 'u_jac',
             'u_f', 'u_move', 'u_hasFlow', 'u_alpha']);
        this.quad = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
        this.lutTex = this.makeTex(gl, gl.LINEAR);
        this.zeroTex = this.makeTex(gl, gl.NEAREST);
        gl.bindTexture(gl.TEXTURE_2D, this.zeroTex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
        this.lutDirty = true;
    }

    onRemove(_map: MaplibreMap, gl: GLContext) {
        this.destroyed = true;
        for (const t of this.tiles.values()) if (t.tex) gl.deleteTexture(t.tex);
        this.tiles.clear();
        for (const w of this.workers) w.terminate();
        this.workers = [];
        this.pending.clear();
        this.inflight = 0;
        this.pumpDirty = true;
        for (const t of this.targets) { gl.deleteFramebuffer(t.fbo); gl.deleteTexture(t.tex); }
        this.targets = [];
        for (const t of [this.lutTex, this.flowTex, this.zeroTex]) if (t) gl.deleteTexture(t);
        if (this.quad) gl.deleteBuffer(this.quad);
        if (this.tileProg) gl.deleteProgram(this.tileProg);
        if (this.finalProg) gl.deleteProgram(this.finalProg);
        this.flowBmp?.close();
        this.flowBmp = null;
        this.flowTex = null;
        this.flowReady = false;
        // الحقل يُعاد جلبه عند الإضافة التالية (الرابط نفسه، من كاش المتصفّح).
        const f = this.flow;
        this.flow = null;
        if (f) queueMicrotask(() => { if (!this.destroyed) this.setFlow(f); });
        this.map = null;
    }

    private makeTex(gl: GLContext, filter: number): WebGLTexture {
        const t = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return t;
    }

    // ── البلاطات ─────────────────────────────────────────────────────────────
    private key(fi: number, z: number, x: number, y: number): string {
        return `${this.frames[fi]?.prefix}/${z}/${x}/${y}`;
    }

    /** بلاطات الشاشة عند مستوى البيانات المناسب لتكبير الخريطة. */
    private computeView(map: MaplibreMap) {
        const b = map.getBounds();
        const x0 = (b.getWest() + 180) / 360, x1 = (b.getEast() + 180) / 360;
        const y0 = Math.max(0, worldY(b.getNorth())), y1 = Math.min(1, worldY(b.getSouth()));
        /*
         * MapLibre يعرض العالم 512·2^Z بكسلاً، فبلاطة 256 عند المستوى z تُعرض بتكبير
         * 2^(Z−z+1). بـ`round` يقع التكبير بين 1.4 و2.8 — Windy يعرض z7 عند تكبير 9 بأربعة
         * أضعاف، والاستيفاء قبل التلوين هو ما يجعل التكبير لا يُرى حافّةً مسنّنة.
         */
        let z = Math.max(0, Math.min(MAX_Z, Math.round(map.getZoom())));
        let out: ViewTile[] = [];
        for (;;) {
            const n = 2 ** z;
            const xa = Math.floor(x0 * n), xb = Math.floor(Math.min(x1 * n, x0 * n + 4 * n - 1e-9));
            const ya = Math.max(0, Math.floor(y0 * n)), yb = Math.min(n - 1, Math.floor(y1 * n - 1e-9));
            out = [];
            for (let xi = xa; xi <= xb; xi++) {
                for (let y = ya; y <= yb; y++) {
                    const x = ((xi % n) + n) % n;
                    out.push({ z, x, y, off: Math.floor(xi / n) });
                }
            }
            if (out.length <= MAX_VIEW_TILES || z === 0) break;
            z -= 1;
        }
        this.view = out;
        this.viewZ = z;
    }

    /** يملأ طابور الجلب: الإطار المعروض والتالي أوّلاً، ثم الحلقة بترتيب التشغيل. */
    private pump() {
        if (!this.frames.length || !this.view.length) return;
        const { i } = this.pairAt(this.epoch);
        // لا يُعاد البناء إلا إن تغيّرت الشاشة أو الإطار أو وصل جديد: كان يُبنى في كل
        // إطار رسم (مئات المفاتيح النصّية ستّين مرّة في الثانية) بلا أن يتغيّر شيء.
        const pk = `${i}|${this.viewZ}|${this.view.length}|${this.view[0].x},${this.view[0].y}`;
        if (pk === this.pumpKey && !this.pumpDirty) { this.drain(); return; }
        this.pumpKey = pk;
        this.pumpDirty = false;
        const n = this.frames.length;
        const order: number[] = [];
        for (let k = 0; k < n; k++) order.push((Math.max(0, i) + k) % n);
        const q: string[] = [];
        // البلاطات الفريدة لكل إطار (نسخ العالم تتقاسم البلاطة نفسها).
        const uniq = new Map<string, ViewTile>();
        for (const v of this.view) uniq.set(`${v.z}/${v.x}/${v.y}`, v);
        for (const fi of order) {
            for (const v of uniq.values()) {
                const k = this.key(fi, v.z, v.x, v.y);
                const t = this.tiles.get(k);
                if (!t || (t.st === St.Failed && t.tries < 3 && t.used < this.stamp - 120)) q.push(k);
            }
        }
        this.queue = q;
        this.drain();
    }

    private worker(): Worker {
        if (!this.workers.length) {
            for (let n = 0; n < 2; n++) {
                const w = new Worker(new URL('../workers/radarTileWorker.ts', import.meta.url), { type: 'module' });
                w.onmessage = (e: MessageEvent<{ id: number; empty?: boolean; failed?: boolean; w?: number; h?: number; px?: Uint8Array }>) => {
                    const m = e.data;
                    const rec = this.pending.get(m.id);
                    if (!rec) return;
                    this.pending.delete(m.id);
                    this.inflight--;
                    if (this.destroyed) return;
                    if (m.px) { rec.px = m.px; rec.w = m.w!; rec.h = m.h!; this.stats.fetched++; }
                    else if (m.empty) { rec.st = St.Empty; this.stats.empty++; }
                    else { rec.st = St.Failed; this.stats.failed++; }
                    this.pumpDirty = true;
                    this.map?.triggerRepaint();
                    this.drain();
                };
                this.workers.push(w);
            }
        }
        return this.workers[this.nextWorker++ % this.workers.length];
    }

    private drain() {
        while (this.inflight < FETCH_CONCURRENCY && this.queue.length) {
            const k = this.queue.shift()!;
            const prev = this.tiles.get(k);
            if (prev && prev.st !== St.Failed) continue;
            const rec: TileTex = {
                st: St.Loading, tex: null, px: null, w: 0, h: 0, used: this.stamp, tries: (prev?.tries ?? 0) + 1,
            };
            this.tiles.set(k, rec);
            this.inflight++;
            const id = ++this.reqId;
            this.pending.set(id, rec);
            const url = new URL(`${this.base}${k}.png`, location.href).href;
            this.worker().postMessage({ id, url, blank: this.blankSize });
        }
    }

    private uploadSome(gl: GLContext) {
        let n = 0;
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        let more = false;
        for (const t of this.tiles.values()) {
            if (!t.px) continue;
            if (n >= UPLOADS_PER_FRAME) { more = true; break; }
            t.tex = this.makeTex(gl, gl.LINEAR);
            // نسخٌ مباشر من مصفوفة بايتات: لا تحويل صيغة ولا قراءة من المعالج.
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, t.w, t.h, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, t.px);
            t.px = null;
            t.st = St.Ready;
            n++;
            this.stats.uploads++;
        }
        if (this.flowBmp) {
            if (!this.flowTex) this.flowTex = this.makeTex(gl, gl.LINEAR);
            gl.bindTexture(gl.TEXTURE_2D, this.flowTex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.flowBmp);
            this.flowBmp.close();
            this.flowBmp = null;
            this.flowReady = true;
        }
        if (this.lutDirty && this.lutTex) {
            gl.bindTexture(gl.TEXTURE_2D, this.lutTex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, this.lut);
            this.lutDirty = false;
        }
        if (more) this.map?.triggerRepaint();
    }

    /** يُبقي الأحدث استعمالاً ويحذف الباقي حين تتجاوز النُّسج السقف. */
    private evict(gl: GLContext) {
        let live = 0;
        for (const t of this.tiles.values()) if (t.tex) live++;
        if (live <= CACHE_MAX && this.tiles.size < CACHE_MAX * 20) return;
        const ents = [...this.tiles.entries()].filter(([, t]) => t.st !== St.Loading)
            .sort((a, b) => a[1].used - b[1].used);
        for (const [k, t] of ents) {
            if (live <= CACHE_MAX * 0.85 && this.tiles.size < CACHE_MAX * 20) break;
            if (t.used >= this.stamp) continue;              // يُعرض الآن
            if (t.tex) { gl.deleteTexture(t.tex); live--; }
            this.tiles.delete(k);
        }
    }

    private ensureTargets(gl: GLContext) {
        // بدقّة الشاشة حتى 1:1، ونصفها على الشاشات الكثيفة: القيمة تُستوفى أصلاً، ونصف
        // الدقّة على شاشة 2x يساوي بكسل CSS واحداً — ربع الكلفة بلا فرقٍ يُرى.
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const s = dpr > 1.5 ? 0.5 : 1;
        const w = Math.max(1, Math.round(gl.drawingBufferWidth * s));
        const h = Math.max(1, Math.round(gl.drawingBufferHeight * s));
        if (this.targets.length === 2 && this.targetSize[0] === w && this.targetSize[1] === h) return;
        for (const t of this.targets) { gl.deleteFramebuffer(t.fbo); gl.deleteTexture(t.tex); }
        this.targets = [0, 1].map(() => {
            const tex = this.makeTex(gl, gl.LINEAR);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            const fbo = gl.createFramebuffer()!;
            gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
            return { fbo, tex };
        });
        this.targetSize = [w, h];
    }

    /**
     * أفضل ما لدينا لبلاطةٍ في إطار: نفسها، ثم سلفها الأخشن (يُعرض ممدّداً ريثما تصل —
     * هكذا يبقى التكبير بلا فراغ)، ثم البلاطة نفسها من أقرب إطارٍ وصل.
     */
    private best(fi: number, v: ViewTile): { t: TileTex; uv: [number, number, number] } | null {
        const own = this.tiles.get(this.key(fi, v.z, v.x, v.y));
        if (own && (own.st === St.Ready || own.st === St.Empty)) return { t: own, uv: [0, 0, 1] };
        for (let k = 1; k <= v.z; k++) {
            const z = v.z - k, x = v.x >> k, y = v.y >> k;
            const p = this.tiles.get(this.key(fi, z, x, y));
            if (p && (p.st === St.Ready || p.st === St.Empty)) {
                const s = 1 / 2 ** k;
                return { t: p, uv: [(v.x - (x << k)) * s, (v.y - (y << k)) * s, s] };
            }
        }
        for (let d = 1; d < this.frames.length; d++) {
            for (const g of [fi - d, fi + d]) {
                if (g < 0 || g >= this.frames.length) continue;
                const o = this.tiles.get(this.key(g, v.z, v.x, v.y));
                if (o && (o.st === St.Ready || o.st === St.Empty)) { this.stats.fallbacks++; return { t: o, uv: [0, 0, 1] }; }
            }
        }
        return null;
    }

    private drawFrame(gl: GLContext, target: Target, fi: number, matrix: Float32Array) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
        gl.viewport(0, 0, this.targetSize[0], this.targetSize[1]);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        if (fi < 0) return;
        const L = this.tl!;
        gl.useProgram(this.tileProg);
        gl.uniformMatrix4fv(L.uniforms.u_matrix, false, matrix);
        gl.uniform1i(L.uniforms.u_tex, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
        gl.enableVertexAttribArray(L.attributes.a_pos);
        gl.vertexAttribPointer(L.attributes.a_pos, 2, gl.FLOAT, false, 0, 0);
        const span = 1 / 2 ** this.viewZ;
        for (const v of this.view) {
            const got = this.best(fi, v);
            if (!got) continue;
            got.t.used = this.stamp;
            const empty = got.t.st === St.Empty || !got.t.tex;
            bindTexture(gl, empty ? this.zeroTex! : got.t.tex!, 0);
            gl.uniform1f(L.uniforms.u_empty, empty ? 1 : 0);
            gl.uniform3f(L.uniforms.u_tile, v.x * span, v.y * span, span);
            gl.uniform3f(L.uniforms.u_uv, got.uv[0], got.uv[1], got.uv[2]);
            gl.uniform1f(L.uniforms.u_worldOffset, v.off);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    // ── الرسم ────────────────────────────────────────────────────────────────
    private pair = { i: -1, j: -1, f: 0 };
    private errorLogged = false;

    prerender(gl: GLContext, options: CustomRenderMethodInput) {
        try {
            const map = this.map;
            if (!map || !this.tileProg) return;
            this.stamp++;
            this.computeView(map);
            this.pump();
            this.uploadSome(gl);
            this.evict(gl);
            this.ensureTargets(gl);
            this.pair = this.pairAt(this.epoch);
            const prevFbo = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
            const prevVp = gl.getParameter(gl.VIEWPORT) as Int32Array;
            gl.disable(gl.BLEND);
            gl.disable(gl.DEPTH_TEST);
            gl.disable(gl.STENCIL_TEST);
            gl.disable(gl.SCISSOR_TEST);
            gl.disable(gl.CULL_FACE);
            const m = new Float32Array(options.defaultProjectionData.mainMatrix as ArrayLike<number>);
            this.drawFrame(gl, this.targets[0], this.pair.i, m);
            this.drawFrame(gl, this.targets[1], this.pair.j, m);
            gl.bindFramebuffer(gl.FRAMEBUFFER, prevFbo);
            gl.viewport(prevVp[0], prevVp[1], prevVp[2], prevVp[3]);
        } catch (e) {
            if (!this.errorLogged) { console.error('RadarFlowGLLayer prerender', e); this.errorLogged = true; }
        }
    }

    render(gl: GLContext, options: CustomRenderMethodInput) {
        try {
            if (!this.finalProg || this.targets.length !== 2 || this.pair.i < 0) return;
            const L = this.fl!;
            const M = options.defaultProjectionData.mainMatrix as ArrayLike<number>;
            gl.useProgram(this.finalProg);
            gl.uniformMatrix4fv(L.uniforms.u_matrix, false, new Float32Array(M));
            /*
             * مشتقّ الإسقاط: إزاحةٌ بوحدة العالم ← إزاحةٌ في إحداثيات الشاشة 0..1. للخريطة
             * المستوية (لا ميل) الإسقاط خطّي والمقام ثابت، فيكفي حسابه عند المركز. ومع
             * الميل يصير تقريبياً — والإزاحة بضعة كيلومترات، فالخطأ دون البكسل.
             */
            const c = this.map!.getCenter();
            const cx = (c.lng + 180) / 360, cy = worldY(c.lat);
            const w = M[3] * cx + M[7] * cy + M[15] || 1;
            gl.uniformMatrix2fv(L.uniforms.u_jac, false, new Float32Array([
                0.5 * M[0] / w, 0.5 * M[1] / w, 0.5 * M[4] / w, 0.5 * M[5] / w,
            ]));
            gl.uniform2f(L.uniforms.u_viewport, gl.drawingBufferWidth, gl.drawingBufferHeight);
            const { i, j, f } = this.pair;
            const F = this.frames;
            const hasFlow = this.flowReady && !!this.flow && !!this.flowTex && i !== j;
            const move = hasFlow ? (F[j].t - F[i].t) / this.flow!.seconds * this.flow!.scale : 0;
            gl.uniform1f(L.uniforms.u_f, i === j ? 0 : f);
            gl.uniform1f(L.uniforms.u_move, move);
            gl.uniform1f(L.uniforms.u_hasFlow, hasFlow ? 1 : 0);
            gl.uniform1f(L.uniforms.u_alpha, this.alpha);
            bindTexture(gl, this.targets[0].tex, 0); gl.uniform1i(L.uniforms.u_a, 0);
            bindTexture(gl, this.targets[1].tex, 1); gl.uniform1i(L.uniforms.u_b, 1);
            bindTexture(gl, hasFlow ? this.flowTex! : this.zeroTex!, 2); gl.uniform1i(L.uniforms.u_flow, 2);
            bindTexture(gl, this.lutTex!, 3); gl.uniform1i(L.uniforms.u_lut, 3);
            gl.disable(gl.DEPTH_TEST);
            gl.disable(gl.STENCIL_TEST);
            gl.disable(gl.CULL_FACE);
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
            gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
            gl.enableVertexAttribArray(L.attributes.a_pos);
            gl.vertexAttribPointer(L.attributes.a_pos, 2, gl.FLOAT, false, 0, 0);
            const offs = new Set(this.view.map((v) => v.off));
            if (!offs.size) offs.add(0);
            for (const off of offs) {
                gl.uniform1f(L.uniforms.u_worldOffset, off);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        } catch (e) {
            if (!this.errorLogged) { console.error('RadarFlowGLLayer render', e); this.errorLogged = true; }
        }
    }
}
